import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import "core-js/modules/es7.symbol.async-iterator";
import "core-js/modules/es6.symbol";
import "core-js/modules/web.dom.iterable";
import _isEqual from "lodash/isEqual";

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import { useState, useContext, useRef, useEffect } from 'react';
import uuid from 'uuid/v4';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import AnalyticsContext from '../AnalyticsContext';
import ImpressionContext from './ImpressionContext';
import { useWait } from '../hooks';
/**
 * Hook that will fire an event when `isVisible` has been true for more than
 * 200ms. This hook is designed to be agnostic to how `isVisible` is determined
 * or where the analytics instance comes from. It should not be used directly;
 * web and mobile have their own specialized wrappers that automatically
 * provide `isVisible`. See `useImpression.js` and `useImpression.native.js`.
 *
 * This hook will automatically refire after an event's payload has changed.
 * This can happen in one of two ways:
 *
 * 1. `eventType`, `entityName`, `elementName`, and/or `payload` props change.
 * 2. The page's `navigation_id` changes during a transition from
 *    "visible" -> "not visible" -> "visible". The most common scenario in
 *    which this will happen is in the mobile app when a user navigates back to
 *    a page that had previously fired an event, since components on screens in
 *    the back stack are generally not unmounted.
 *
 * In case this behavior is not desirable, this can be toggled off with the
 * `refireOnEventChange` flag. If this flag is set to true, this hook is
 * guaranteed to only ever fire at most one event.
 */

var useImpressionImpl = function useImpressionImpl(_ref) {
  var _ref$eventType = _ref.eventType,
      eventType = _ref$eventType === void 0 ? 'element_impression' : _ref$eventType,
      elementName = _ref.elementName,
      entityName = _ref.entityName,
      _ref$payload = _ref.payload,
      payload = _ref$payload === void 0 ? {} : _ref$payload,
      _ref$refireOnEventCha = _ref.refireOnEventChange,
      refireOnEventChange = _ref$refireOnEventCha === void 0 ? true : _ref$refireOnEventCha,
      _ref$isVisible = _ref.isVisible,
      isVisible = _ref$isVisible === void 0 ? false : _ref$isVisible;

  // Don't generate a UUID immediately; wait until after the first render.
  // This ensures that there won't be a DOM mismatch during React hydration
  // if this ID ends up somewhere in the DOM (for instance, in an `href`).
  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      impressionId = _useState2[0],
      setImpressionId = _useState2[1];

  useEffect(function () {
    setImpressionId(uuid());
  }, []); // Pull the analytics instance from Context.

  var analytics = useContext(AnalyticsContext); // If there's an Impression component above us in the component tree, we'll
  // consume its impressionId and use it for the `parent_impression_id`
  // property on the payload.

  var parentImpression = useContext(ImpressionContext) || {}; // We use `useRef` instead of `useState` because we don't need to rerender
  // after an event is fired, and also because React doesn't guarantee that a
  // component will always render with the latest state. If we used `useState`
  // instead, we could end up with duplicate events.

  var previousEvent = useRef(null); // Compute the payload ahead of time - this is what we'll use to decide if
  // we need to refire an event. If the payload is different from the
  // previously-fired event (possibly "null" if no event has been fired yet),
  // we need to fire a new event.

  var newEvent = {
    eventType: eventType,
    payload: _objectSpread({
      impression_id: impressionId,
      parent_impression_id: parentImpression.impressionId,
      element_name: elementName,
      entity_name: entityName,
      // Normally this would be provided by the `impression` event decorator,
      // but we need to explicitly include it here so that we can correctly
      // diff payloads.
      navigation_id: analytics.getState('navigation_id')
    }, payload)
  }; // The event is serialized with JSON.stringify so that `useEffect` can
  // correctly identify when it should rerun - if we passed the whole object,
  // it would only do a shallow comparison.

  useWait([isVisible, refireOnEventChange, JSON.stringify(newEvent)], 200, function () {
    // This shouldn't ever happen, but if we're running on the client and
    // haven't yet hit the `useEeffect` to generate a UUID, don't fire an
    // event yet. When the UUID is generated, we'll end up here again shortly.
    if (impressionId === null) return; // This conditional covers the two potential cases of
    // `refireOnEventChange`:
    // * If it should refire on event change, fire an event if the new event
    //   does not match the previously fired event.
    // * If it should not refire on event change, fire an event only if no
    //   event was previously fired.

    if (isVisible && (refireOnEventChange && !_isEqual(newEvent, previousEvent.current) || !refireOnEventChange && previousEvent.current === null)) {
      analytics.track(newEvent.eventType, newEvent.payload);
      previousEvent.current = newEvent;
    }
  }); // prefer `entityName` over `elementName`

  useDeprecatedPropsWarning({
    component: 'useImpression',
    deprecatedProps: [{
      version: '1.19.0',
      deprecated: 'elementName',
      migration: 'Use entityName instead.'
    }],
    props: {
      elementName: elementName
    }
  });
  return {
    impressionId: impressionId
  };
};

export default useImpressionImpl;